import { useState, useEffect, useLayoutEffect } from 'react';
import { useParams } from '@tanstack/react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  publicQuoteApi,
  letterheadUrl,
  type PublicQuoteItem,
  type SubmitErrorCode,
} from '../features/publicQuote/api.ts';
import { Icon } from '../components/Icon.tsx';
import { t } from '../lib/strings.ts';
import { formatDate } from '@/lib/format';
import { useCountdown } from '../lib/useCountdown.ts';

// ── Helpers ─────────────────────────────────────────────────────────────────

function errorMessage(code: SubmitErrorCode): string {
  switch (code) {
    case 'id_card_mismatch':
      return t.publicQuote.errIdCardMismatch;
    case 'already_submitted':
      return t.publicQuote.errAlreadySubmitted;
    case 'request_closed':
      return t.publicQuote.errRequestClosed;
    case 'no_prices':
      return t.publicQuote.errNoPrices;
    case 'declaration_required':
      return t.publicQuote.errDeclarationRequired;
    case 'invalid_lines':
    case 'invalid_price':
      return t.publicQuote.errInvalidPrice;
    case 'not_found':
      return t.publicQuote.errNotFound;
  }
}

// ── Price row state ──────────────────────────────────────────────────────────

interface LineState {
  // A blank rate IS the "not quoting this item" signal — it already submits as
  // unitPrice: null, which is exactly what the old explicit checkbox sent.
  priceStr: string; // raw string from input
}

function initLines(items: PublicQuoteItem[]): Record<string, LineState> {
  const out: Record<string, LineState> = {};
  for (const item of items) {
    out[item.id] = { priceStr: '' };
  }
  return out;
}

// ── Component ────────────────────────────────────────────────────────────────

export function PublicQuote(): JSX.Element {
  const { token } = useParams({ from: '/public-layout/q/$token' });

  // This page is a paper document, so it is always light — same guard Login and
  // the vendor portal use. Without it a stored dark preference would flip the
  // status screens' text tokens to light-on-white inside the sheet.
  useLayoutEffect(() => {
    const html = document.documentElement;
    html.classList.remove('dark');
    return () => {
      try {
        const stored = localStorage.getItem('wappro.theme');
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        if (stored === 'dark' || (!stored && prefersDark)) html.classList.add('dark');
      } catch {
        /* ignore */
      }
    };
  }, []);

  // Form state
  const [lines, setLines] = useState<Record<string, LineState>>({});
  const [declared, setDeclared] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-quote', token],
    queryFn: () => publicQuoteApi.get(token),
    retry: false,
  });

  // Initialise line state when data first arrives
  useEffect(() => {
    if (data) setLines(initLines(data.items));
  }, [data]);

  // Called unconditionally, before the early returns below, so hook order stays
  // stable across the loading / closed / submitted branches.
  const cd = useCountdown(data?.deadline);

  const mutation = useMutation({
    mutationFn: (payload: Parameters<typeof publicQuoteApi.submit>[1]) =>
      publicQuoteApi.submit(token, payload),
    onSuccess: () => {
      setSubmitted(true);
      setSubmitError(null);
    },
    onError: (err: unknown) => {
      const axiosErr = err as {
        response?: { data?: { code?: string; error?: string } };
      };
      const code = axiosErr.response?.data?.code as SubmitErrorCode | undefined;
      if (code) {
        setSubmitError(errorMessage(code));
      } else {
        setSubmitError(t.publicQuote.errGeneral);
      }
    },
  });

  // ── Derived ───────────────────────────────────────────────────────────────

  function runningTotal(): number {
    if (!data) return 0;
    return data.items.reduce((sum, item) => {
      const ls = lines[item.id];
      if (!ls) return sum;
      const price = parseFloat(ls.priceStr);
      if (isNaN(price) || price < 0) return sum;
      return sum + price * item.qty;
    }, 0);
  }

  function hasAtLeastOnePrice(): boolean {
    return Object.values(lines).some((ls) => {
      const p = parseFloat(ls.priceStr);
      return !isNaN(p) && p >= 0;
    });
  }

  const canSubmit = hasAtLeastOnePrice() && declared;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!data) return;
    setSubmitError(null);

    const submitLines = data.items.map((item) => {
      const ls = lines[item.id] ?? { priceStr: '' };
      const unitPrice = parseFloat(ls.priceStr);
      return {
        requestItemId: item.id,
        // Blank / unparseable → null: this item is not being quoted.
        unitPrice: isNaN(unitPrice) ? null : unitPrice,
      };
    });

    mutation.mutate({
      confirmedDeclaration: true,
      lines: submitLines,
    });
  }

  function setLine(itemId: string, patch: Partial<LineState>) {
    setLines((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], ...patch } as LineState,
    }));
  }

  // ── Render states ─────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <PublicShell>
        <p className="text-center text-muted-foreground py-16 animate-pulse">
          {t.publicQuote.loadingForm}
        </p>
      </PublicShell>
    );
  }

  if (isError || !data) {
    return (
      <PublicShell>
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <Icon name="cancel" size={40} className="text-destructive" />
          <p className="text-sm text-destructive font-medium">
            {t.publicQuote.invalidLinkTitle}
          </p>
        </div>
      </PublicShell>
    );
  }

  if (submitted) {
    return (
      <PublicShell councilName={data.councilName}>
        <div className="flex flex-col items-center gap-4 py-16 text-center">
          <Icon name="check_circle" size={48} className="text-[hsl(var(--success))]" />
          <h2 className="text-xl font-bold text-ink">
            {t.publicQuote.thankYouTitle(data.vendorName)}
          </h2>
          <p className="text-sm text-muted-foreground max-w-sm">
            {t.publicQuote.thankYouBody(data.requestTitle, data.refNumber)}
          </p>
        </div>
      </PublicShell>
    );
  }

  if (data.status === 'submitted') {
    return (
      <PublicShell councilName={data.councilName}>
        <div className="flex flex-col items-center gap-4 py-16 text-center">
          <Icon name="check_circle" size={48} className="text-[hsl(var(--success))]" />
          <h2 className="text-xl font-bold text-ink">
            {t.publicQuote.alreadySubmittedTitle}
          </h2>
          <p className="text-sm text-muted-foreground max-w-sm">
            {t.publicQuote.alreadySubmittedBody(data.requestTitle, data.refNumber)}
          </p>
        </div>
      </PublicShell>
    );
  }

  if (data.status === 'closed') {
    return (
      <PublicShell councilName={data.councilName}>
        <div className="flex flex-col items-center gap-4 py-16 text-center">
          <Icon name="schedule" size={48} className="text-muted-foreground" />
          <h2 className="text-xl font-bold text-ink">{t.publicQuote.closedTitle}</h2>
          <p className="text-sm text-muted-foreground max-w-sm">
            {t.publicQuote.closedBody(data.requestTitle, data.refNumber)}
          </p>
        </div>
      </PublicShell>
    );
  }

  // status === 'open'
  const total = runningTotal();

  return (
    <PublicShell councilName={data.councilName} sheet={false}>
      {/* ── Workflow strip ─────────────────────────────────────────────────
          Time left and the specification sheet are things the vendor needs
          BEFORE pricing, and neither belongs on a quotation. They sit above
          the sheet, on the desk, so the document below stays a document. */}
      {(data.deadline || data.specDoc) && (
        <div className="mb-4 space-y-3">
          {data.deadline && (
            <div className="rounded-lg bg-white px-5 py-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
                <div className="min-w-0">
                  <div className="text-[10pt] tracking-wide text-[#777]">
                    {cd.passed ? t.time.passed : t.time.remaining}
                  </div>
                  {!cd.passed && (
                    <div className="mt-0.5 text-[10pt] text-[#666]">
                      {t.publicQuote.deadline}{' '}
                      <span className="num">{formatDate(data.deadline)}</span>
                    </div>
                  )}
                </div>

                {cd.passed ? (
                  <div className="flex items-center gap-2 text-[#bd3c2c]">
                    <Icon name="schedule" size={22} />
                    <span className="text-[13pt] font-bold">{t.time.passed}</span>
                  </div>
                ) : (
                  // No dir="ltr" here: the tiles must flow RTL so the largest
                  // unit (days) reads first, on the right. Only the digits
                  // inside are LTR-isolated, via `.num`.
                  <div className="flex items-center gap-2">
                    {[
                      ...(cd.days > 0 ? [{ v: cd.days, l: t.time.days }] : []),
                      { v: cd.hours, l: t.time.hours },
                      { v: cd.minutes, l: t.time.minutes },
                      ...(cd.days === 0 ? [{ v: cd.seconds, l: t.time.seconds }] : []),
                    ].map((p, i) => (
                      <div key={i} className="countdown-tile">
                        <span className="num tabular block text-[16pt] font-bold leading-none">
                          {String(p.v).padStart(2, '0')}
                        </span>
                        <span className="mt-1 block text-[8.5pt] text-[#5b6b7f]">{p.l}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {data.specDoc && (
            <a
              href={`/api/public/quote/${token}/spec-doc`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-lg bg-white px-5 py-3.5 shadow-sm transition-colors hover:bg-[#f7f9fb]"
            >
              <Icon name="description" size={20} className="shrink-0 text-[#1e3a5f]" />
              <span className="min-w-0 flex-1">
                <span className="block text-[11pt] font-medium text-[#1e3a5f]">
                  {t.publicQuote.specDocLabel}
                </span>
                <span className="block truncate text-[9.5pt] text-[#777]" dir="ltr">
                  {data.specDoc.name}
                </span>
              </span>
              <Icon name="download" size={18} className="shrink-0 text-[#1e3a5f]" />
            </a>
          )}
        </div>
      )}

      <form id="quote-form" onSubmit={handleSubmit} noValidate>
        {/* ── The quotation sheet ────────────────────────────────────────────
            A mirror of backend/src/templates/quote/quotation.html: same order,
            same column set, same navy rules. The vendor fills in the rate
            column of their OWN quotation rather than a web form that later
            becomes one. Deliberately not the app's M3 chrome — this is a
            document. */}
        <article className="doc-sheet">
          {/* Letterhead band, or the typed issuer block when they have none —
              the same either/or the PDF applies. */}
          {data.hasHeaderImage ? (
            <img
              src={letterheadUrl(token, 'header')}
              alt=""
              className="mb-4 block w-full object-contain"
            />
          ) : null}

          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0">
              <div className="text-[19pt] font-bold leading-tight text-[#111]">{data.vendorName}</div>
              {data.vendorAddress && (
                <div className="mt-0.5 text-[11.5pt] text-[#444]">{data.vendorAddress}</div>
              )}
              <div className="mt-0.5 text-[11.5pt] text-[#444]">
                {t.quotationDoc.phone} <span className="num">{data.vendorPhone}</span>
              </div>
            </div>
            <div className="shrink-0 text-end">
              <div className="text-[22pt] font-bold tracking-wide text-[#1e3a5f]">
                {t.quotationDoc.title}
              </div>
              <div className="mt-1 text-[11.5pt] text-[#444]">
                <span className="text-[#777]">{t.quotationDoc.refNumber}</span>{' '}
                <span className="num">{data.refNumber}</span>
              </div>
              <div className="mt-0.5 text-[11.5pt] text-[#444]">
                <span className="text-[#777]">{t.quotationDoc.date}</span>{' '}
                <span className="num">{data.issueDate}</span>
              </div>
            </div>
          </div>

          <hr className="my-4 border-0 border-t-2 border-[#1e3a5f]" />

          <div className="mb-5">
            <div className="mb-1 text-[10pt] tracking-wide text-[#777]">{t.quotationDoc.toLabel}</div>
            <div className="text-[13.5pt] font-bold text-[#111]">{data.councilName}</div>
          </div>

          {/* Items. The table FITS the viewport rather than scrolling: a
              scroll container here hid the item-name column on phones (RTL
              containers don't reliably start at the inline-start), and the
              item name is the one column a vendor must read to price the row.
              overflow-x-auto stays only as a last-resort guard so a freak
              content width can never push the PAGE sideways. */}
          <div className="-mx-1 overflow-x-auto px-1">
            <table className="doc-items w-full border-collapse">
              <thead>
                <tr className="bg-[#1e3a5f] text-white">
                  <th className="w-7 px-1 py-[7px] text-center font-normal sm:w-9 sm:px-2">#</th>
                  <th className="px-1 py-[7px] text-start font-normal sm:px-2">{t.quotationDoc.colItem}</th>
                  <th className="w-[46px] px-1 py-[7px] text-end font-normal sm:w-[86px] sm:px-2">{t.quotationDoc.colQty}</th>
                  <th className="w-[52px] px-1 py-[7px] text-start font-normal sm:w-[80px] sm:px-2">{t.quotationDoc.colUnit}</th>
                  <th className="w-[92px] px-1 py-[7px] text-end font-normal sm:w-[136px] sm:px-2">{t.quotationDoc.colRate}</th>
                  <th className="w-[72px] px-1 py-[7px] text-end font-normal sm:w-[112px] sm:px-2">{t.quotationDoc.colLineTotal}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item, idx) => {
                  const ls = lines[item.id] ?? { priceStr: '' };
                  const price = parseFloat(ls.priceStr);
                  const priced = ls.priceStr !== '' && !isNaN(price) && price >= 0;
                  return (
                    <tr key={item.id} className="border-b border-[#e5e5e5] last:border-b-0">
                      <td className="px-1 py-1.5 text-center align-top text-[#555] sm:px-2">
                        <span className="num">{idx + 1}</span>
                      </td>
                      <td className="px-1 py-1.5 align-top text-[#111] sm:px-2">
                        {item.name}
                        {item.spec && (
                          <span className="block text-[10pt] text-[#777]">{item.spec}</span>
                        )}
                      </td>
                      <td className="px-1 py-1.5 text-end align-top sm:px-2">
                        <span className="num">{item.qty}</span>
                      </td>
                      <td className="px-1 py-1.5 align-top text-[#444] sm:px-2">{item.unit}</td>

                      {/* The ONLY editable cell on the document. */}
                      <td className="px-1 py-1 align-top sm:px-2">
                        <input
                          id={`price-${item.id}`}
                          type="number"
                          min="0"
                          step="0.01"
                          aria-label={`${t.quotationDoc.colRate} — ${item.name}`}
                          className="doc-rate-input num"
                          placeholder="0.00"
                          value={ls.priceStr}
                          onChange={(e) => setLine(item.id, { priceStr: e.target.value })}
                          inputMode="decimal"
                          dir="ltr"
                        />
                      </td>

                      <td className="px-1 py-1.5 text-end align-top text-[#111] sm:px-2">
                        {priced ? (
                          <span className="num">{(item.qty * price).toFixed(2)}</span>
                        ) : (
                          <span className="text-[#bbb]">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Grand total — navy rule, inline-start like the template. */}
          <div className="mt-4 flex justify-start">
            <div className="min-w-[260px] border-t-2 border-[#1e3a5f] pt-2">
              <div className="flex items-baseline justify-between gap-6 text-[14pt] font-bold">
                <span>{t.quotationDoc.grandTotal}</span>
                <span className="num">{total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Confirmation — where the generated PDF stamps the vendor's
              confirmation, so signing happens on the document itself. */}
          <div className="mt-10 border-t border-[#e5e5e5] pt-5">
            <div className="max-w-md space-y-4">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-0.5 size-5 shrink-0 cursor-pointer rounded accent-[#1e3a5f]"
                  checked={declared}
                  onChange={(e) => setDeclared(e.target.checked)}
                />
                <span className="text-[11.5pt] leading-relaxed text-[#333]">
                  {t.publicQuote.declarationText(data.vendorName)}
                </span>
              </label>

              {submitError && (
                <div
                  role="alert"
                  className="rounded-lg border border-[#bd3c2c]/30 bg-[#bd3c2c]/5 px-4 py-3 text-[11pt] text-[#bd3c2c]"
                >
                  {submitError}
                </div>
              )}

              <button
                type="submit"
                form="quote-form"
                disabled={!canSubmit || mutation.isPending}
                className="doc-submit"
              >
                {mutation.isPending ? t.publicQuote.submitting : t.publicQuote.submit}
              </button>

              <p className="text-center text-[10pt] text-[#888]">{t.publicQuote.submitOnce}</p>
            </div>
          </div>

          {/* Footer note — the vendor's own line, or the council's when they
              have no letterhead. The backend guarantees only one is set. */}
          {(data.letterheadFooterText || data.footerNote) && (
            <div className="mt-8 text-[10pt] text-[#888]">
              {data.letterheadFooterText || data.footerNote}
            </div>
          )}

          {data.hasFooterImage ? (
            <img
              src={letterheadUrl(token, 'footer')}
              alt=""
              className="mt-6 block w-full object-contain"
            />
          ) : null}
        </article>
      </form>
    </PublicShell>
  );
}

// ── Standalone shell (no sidebar/header) ─────────────────────────────────────

function PublicShell({
  children,
  councilName,
  // The open state renders its own <article class="doc-sheet">; the status
  // screens (loading / invalid / submitted / closed) get one from here so they
  // sit on paper too rather than bare on the desk.
  sheet = true,
}: {
  children: React.ReactNode;
  councilName?: string;
  sheet?: boolean;
}) {
  return (
    // A neutral desk the sheet lies on — not the app's chrome, so the document
    // reads as a document. Fixed light scheme: this is paper.
    <div className="min-h-screen bg-[#eceff1]" style={{ colorScheme: 'light' }}>
      {/* Slim top bar */}
      <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-black/10 bg-white/80 px-4 py-3 backdrop-blur-md">
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-semibold text-[#111]">
            {councilName ?? 'WapPro'}
          </p>
          <p className="text-xs text-[#666]">{t.publicQuote.vendorQuoteForm}</p>
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto w-full max-w-[880px] px-3 py-6 sm:px-6 sm:py-10">
        {sheet ? <div className="doc-sheet">{children}</div> : children}
      </main>
    </div>
  );
}
